import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { FiTrash2, FiDownload, FiFileText } from 'react-icons/fi';
import MapModal from '../../MapModal';
import 'leaflet/dist/leaflet.css';

const Container = styled.div`
  padding: 1rem;
  background-color: #f0f4f8;
  text-align: left;
  border-radius: 5px;

  p, a {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  @media (max-width: 768px) {
    text-align: center;
    p {
      display: flex;
      flex-direction: column;
      font-size: 14px;
    }
  }
`;

const SinglePhotoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 10px;
  margin-bottom: 20px;

  img {
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 10px;
  margin-bottom: 20px;

  img {
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  pointer-events: ${(props) => (props.visible ? 'auto' : 'none')};
  transition: visibility 0.3s ease;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const FileListContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 50px;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 12px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  margin-right: 20px;

  svg {
    color: #385963;
    font-size: 20px;
  }

  span {
    font-weight: 500;
    color: #333;
    font-size: 16px;

    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
`;

const FileActions = styled.div`
  display: flex;
  gap: 10px;

  button, a {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    color: #385963;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    padding: 5px 10px;
    border-radius: 6px;
    text-decoration: none;
    transition: background-color 0.2s ease;

    svg {
      font-size: 18px;
    }

    &:hover {
      background-color: #d6e4e9;
      color: #2e4852;
    }
  }

  button.delete {
    color: #e74c3c;

    &:hover {
      background-color: #f9d6d5;
      color: #c0392b;
    }
  }

  @media (max-width: 768px) {
    gap: 10px;
    button, a {
      padding: 2px;
    }
  }
`;

// FilePlaceTypeEnum
const FILE_PLACE_TYPE = {
  Gallery: 0,
  CategoryPhoto: 1,
  CoverPhoto: 2,
  Poster: 3,
  MiscFile: 4
};

const AmenityDetails = ({ amenity }) => {
  const { t, i18n } = useTranslation();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const tokenString = sessionStorage.getItem("token");
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const filesInputRef = useRef(null);
  const modalCleanupRef = useRef(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Initialize form with default structure to avoid undefined errors
  const [form, setForm] = useState({
    name: { gr: '', en: '' },
    description: { gr: '', en: '' },
    location: { latitude: '', longitude: '' },
    websiteUrl: '',
    link360View: '',
    panoramicView: '',
    categoryId: '',
    coverPhoto: null,
    categoryPhoto: null,
    photos: [],
    existingCoverFileName: null,
    existingCategoryFileName: null,
    existingGalleryFileNames: [],
    existingLegalFiles: [],
    legalFiles: [],
    legalFilesBase64Srcs: [],
  });

  const openPreview = (url) => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }

    if (isMobile) {
      window.open(url, '_blank');
    } else {
      setPdfPreviewUrl(url);
      setModalVisible(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${BASE_URL}Amenity/${amenity.id}`, {
          headers: {
            Authorization: `Bearer ${tokenString}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch amenity data");
        const data = await response.json();

        const rawFileNames = data.fileNames || [];

        let fetchedFiles = [];

        if (rawFileNames.length > 0) {
          const fileResponse = await fetch(`${BASE_URL}Amenity/getFiles`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokenString}`,
            },
            body: JSON.stringify(rawFileNames),
          });

          if (!fileResponse.ok) throw new Error("Failed to fetch file contents");
          fetchedFiles = await fileResponse.json();
        }

        const {
          existingCoverFileName,
          existingCategoryFileName,
          existingGalleryFileNames,
          galleryBase64Srcs,
          coverBase64Src,
          categoryBase64Src,
          existingLegalFiles,
          legalFilesBase64Srcs,
          existingMiscFiles,
          miscFilesBase64Srcs
        } = processFiles(fetchedFiles);

        setForm({
          name: data.name || { gr: '', en: '' },
          description: data.description || { gr: '', en: '' },
          location: data.location || { latitude: '', longitude: '' },
          link360View: data.link360View || '',
          panoramicView: data.panoramicView || '',
          categoryId: data.categoryId || '',
          coverPhoto: null,
          categoryPhoto: null,
          photos: [],
          existingCoverFileName,
          existingCategoryFileName,
          existingGalleryFileNames,
          galleryBase64Srcs,
          coverBase64Src,
          categoryBase64Src,
          legalFiles: [],
          existingLegalFiles,
          legalFilesBase64Srcs,
          existingMiscFiles,
          miscFilesBase64Srcs,
        });

      } catch (err) {
        console.error('Error loading amenity:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();

    // Cleanup on unmount for any URLs created
    return () => {
      if (modalCleanupRef.current) {
        modalCleanupRef.current();
      }
    };
  }, [BASE_URL, amenity.id, tokenString]);

  const processFiles = (files) => {
    const coverFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CoverPhoto);
    const categoryFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CategoryPhoto);
    const galleryFiles = files.filter(f => f.filePlaceType === FILE_PLACE_TYPE.Gallery);
    const legalFiles = files.filter(f => f.filePlaceType === FILE_PLACE_TYPE.MiscFile);

    const coverBase64Src = coverFile ? `data:${coverFile.mimeType};base64,${coverFile.base64Content}` : null;
    const categoryBase64Src = categoryFile ? `data:${categoryFile.mimeType};base64,${categoryFile.base64Content}` : null;
    const galleryBase64Srcs = galleryFiles.map(f => `data:${f.mimeType};base64,${f.base64Content}`);

    const legalFilesBase64Srcs = legalFiles.map(legalFile => ({
      fileName: legalFile.fileName,
      dataUrl: `data:${legalFile.mimeType};base64,${legalFile.base64Content}`
    }));

    const existingLegalFiles = legalFiles.map(f => f.fileName);
    const existingGalleryFileNames = galleryFiles.map(f => f.fileName);

    return {
      existingCoverFileName: coverFile?.fileName || null,
      existingCategoryFileName: categoryFile?.fileName || null,
      existingGalleryFileNames,
      galleryBase64Srcs,
      coverBase64Src,
      categoryBase64Src,
      existingLegalFiles,
      legalFilesBase64Srcs,
      miscFilesBase64Srcs: legalFilesBase64Srcs,
      existingMiscFiles: existingLegalFiles
    };
  };

  const handleOpenModal = (img) => {
    if (!img) return;

    if (typeof img === 'string') {
      setModalImage(img);
      setShowModal(true);
    } else {
      const url = URL.createObjectURL(img);
      setModalImage(url);
      setShowModal(true);

      modalCleanupRef.current = () => {
        URL.revokeObjectURL(url);
        modalCleanupRef.current = null;
      };
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (modalCleanupRef.current) {
      modalCleanupRef.current();
    } else {
      setModalImage('');
    }
  };




function getCleanFileName(fileName, index) {
  if (!fileName) return `File ${index + 1}`;

  // Remove "MiscFile_" prefix if it exists
  let cleanedName = fileName.replace(/^MiscFile_/, '');

  // Remove GUIDs (UUID format)
  cleanedName = cleanedName.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '');

  // Optional: replace underscores/dashes with spaces (depends on your naming)
  cleanedName = cleanedName.replace(/[_-]+/g, ' ').trim();

  // Ensure there's still a valid file name
  const extensionMatch = fileName.match(/\.[0-9a-z]+$/i);
  const extension = extensionMatch ? extensionMatch[0] : '';

  if (!cleanedName || cleanedName === '.' || /^\.[a-z]+$/i.test(cleanedName)) {
    return `Document ${index + 1}${extension}`;
  }

  return `${cleanedName}${extension}`;
}




  return (
    <Container>
      <p><strong>{i18n.t('name')} (English):</strong> {amenity.name.en}</p>
      <p><strong>{i18n.t('name')} (Ελληνικά):</strong> {amenity.name.gr}</p>
      <p><strong>{i18n.t('description')} (English):</strong> {amenity.description.en}</p>
      <p><strong>{i18n.t('description')} (Ελληνικά):</strong> {amenity.description.gr}</p>
      <p><strong>{i18n.t('category')} (English):</strong> {amenity.categoryName.en}</p>
      <p><strong>{i18n.t('category')} (Ελληνικά):</strong> {amenity.categoryName.gr}</p>
      {/* <p><strong>{i18n.t('location')}</strong></p>
      <p><strong>{i18n.t('latitude')}:</strong> {amenity.location.latitude}</p>
      <p><strong>{i18n.t('longitude')}:</strong> {amenity.location.longitude}</p> */}
       <p style={{ cursor: 'pointer', textDecoration:'underline' }} 
        onClick={() => {
          setSelectedPosition([amenity.location.latitude, amenity.location.longitude]);
          setShowMapModal(true);
        }}><strong>{i18n.t('location')}:</strong>
         {amenity.location.longitude}, {amenity.location.latitude}
      </p>
      <p><strong>{i18n.t('phone')}:</strong> {amenity.phone}</p>
      <p><strong>{i18n.t('email')}:</strong> {amenity.email}</p>
      {amenity.websiteUrl && (
      <p>
        <strong>{i18n.t('websiteUrl')}:</strong>{" "}
        <a 
          href={amenity.websiteUrl.startsWith('http') ? amenity.websiteUrl : `https://${amenity.websiteUrl}`} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          {amenity.websiteUrl}
        </a>
      </p>
    )}
      <p><strong>{i18n.t('providerName')}:</strong> {amenity.providerName}</p>

      <FileListContainer>
        {form.miscFilesBase64Srcs?.length > 0 && (
          <>
           <p> <strong>{t('files')}:</strong></p>
            {form.miscFilesBase64Srcs.map(({ fileName, dataUrl }, index) => (
              <FileCard key={`misc-${index}`}>
                <FileInfo>
                  {/* <span>{fileName}</span> */}
                  <span>{getCleanFileName(fileName, index)}</span>
                </FileInfo>
                <FileActions>
                  <a
                    href={dataUrl}
                    // target="_blank"
                    // rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreview(dataUrl);
                    }}
                    title={t('preview')}
                  >
                    <FiFileText />
                  </a>
                  <a
                    href={dataUrl}
                    download={fileName}
                    onClick={(e) => e.stopPropagation()}
                    title={t('download')}
                  >
                    <FiDownload />
                  </a>
                </FileActions>
              </FileCard>
            ))}
          </>
        )}
      </FileListContainer>

      {/* {form.coverBase64Src && (
        <SinglePhotoContainer>
          <h3>{t('coverPhoto')}:</h3>
          <img
            src={form.coverBase64Src}
            alt="Cover"
            onClick={() => handleOpenModal(form.coverBase64Src)}
            style={{ cursor: 'pointer', maxWidth: '300px', maxHeight: '300px' }}
          />
        </SinglePhotoContainer>
      )} */}

      {form.categoryBase64Src && (
        <SinglePhotoContainer>
         <p> <strong>{t('categoryPhoto')}:</strong></p>
          <img
            src={form.categoryBase64Src}
            alt="Category"
            onClick={() => handleOpenModal(form.categoryBase64Src)}
            style={{ cursor: 'pointer', maxWidth: '300px', maxHeight: '300px' }}
          />
        </SinglePhotoContainer>
      )}

      {form.galleryBase64Srcs?.length > 0 && (
        <PhotoItem>
         <p> <strong>{t('galleryPhotos')}:</strong></p>
          {form.galleryBase64Srcs.map((src, index) => (
            <img
              key={`gallery-${index}`}
              src={src}
              alt={`Gallery ${index}`}
              onClick={() => handleOpenModal(src)}
              style={{ cursor: 'pointer', maxWidth: '150px', maxHeight: '150px', marginRight: '10px' }}
            />
          ))}
        </PhotoItem>
      )}

      <Modal visible={showModal} onClick={handleCloseModal}>
        <ModalImage
          src={modalImage}
          alt="Preview"
          onClick={(e) => e.stopPropagation()}
        />
      </Modal>

      {/* PDF Preview Modal */}
      <Modal visible={modalVisible} onClick={() => setModalVisible(false)}>
        {pdfPreviewUrl && (
          <iframe
            src={pdfPreviewUrl}
            title="PDF Preview"
            width="90%"
            height="90%"
            frameBorder="0"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </Modal>

        {showMapModal && selectedPosition && (
          <MapModal
            position={selectedPosition}
            onClose={() => setShowMapModal(false)}
          />
        )}
    </Container>
  );
};

export default AmenityDetails;
